/**
 * The latent-health panel (P10.4, `V19`, `plan §8.2`).
 *
 * **The displayed heading and this module's name differ on purpose** (`V79`, P14.11). The card
 * is labelled `active latent dimensions`; everything internal -- this file, `#health`,
 * `.card.health`, the four verdict classes, `latent_stats.json` -- keeps the name it has had
 * since `V19`, because renaming those churns three planning documents and every selector in the
 * stylesheet for nothing a viewer can see, and breaks the citation chain from `V19` forward.
 *
 * **It exists because the map cannot answer the question it is asked.** A collapsed model still
 * produces a plausible-looking blob — points spread across the plane, clusters, structure. Nothing
 * about a scatter plot distinguishes "this latent space encodes the corpus" from "this latent
 * space is noise the decoder ignores". `latent_stats.json` answers it, and the answer belongs
 * beside the map rather than in a notebook.
 *
 * **A read, not a computation.** `peptide_vae.evaluation.latent.LatentStats` already produces
 * every number here, `build_atlas` runs it in the same forward pass that writes the latents, and
 * this renders what it wrote. Recomputing in the browser would be a second definition of
 * `collapse_fraction`.
 *
 * **`n_rows` is displayed, and that is the `V11` disclosure in a second place.** A figure computed
 * over a subsample and one computed over the corpus are different numbers; a panel that does not
 * say which it is showing invites the reader to assume the flattering one.
 *
 * ## Scope: the panel shows what training logs to W&B, and nothing it would have to invent
 *
 * Decided 2026-08-20. `peptide_vae`'s latent evaluation logs exactly ten quantities — the nine
 * scalars `LatentStats.compute()` returns, and the per-dimension KL **distribution** that
 * `LatentDiagnostics` ships as a `wandb.Histogram` under `val/latent/kl_per_dim`. **All ten are
 * rendered here**, which is the bar: a figure on this panel and the same figure on a W&B run
 * should be the same number with the same name, and anything beyond that surface would be a second
 * definition of latent health computed in a browser.
 *
 * `active_fraction` appears as `active units — N of D` rather than as its own row, which is the
 * same quantity in the form a reader can act on.
 *
 * **The surface grew by one at `D124`.** `LatentStats.per_dim_mu_variance()` now exists and
 * `LatentDiagnostics` ships it as `val/latent/mu_variance_per_dim`, so the **activity**
 * distribution is in scope and is charted beside the KL. The two answer different questions: the
 * KL says which dimensions the objective pushed to the prior, the activity says which ones the
 * encoder output actually varies across inputs, and `active_units` is a count off the second.
 *
 * **And by one more at `D125`**: `per_dim_sigma_sq` / `latent/sigma_sq_mean`, the mean encoder
 * variance. With the activity it is a per-dimension **budget** —
 * `Cov_q(z)[z]_d = Var_x[μ]_d + E_x[Σ]_d` — so under a matched aggregate posterior the pair sums
 * to about 1 and the *split* is the diagnostic. Either bar alone is unreadable: tiny activity
 * means a dead dimension only if the variance term makes up the rest.
 *
 * **The aggregate spread is deliberately not a stored quantity.** That identity is exact, so it is
 * an addition rather than a measurement — and accumulating sampled `z` instead would produce an
 * estimate carrying reparameterization noise, which could then disagree with the two terms beside
 * it.
 *
 * The IVELS Level 2.1 chart — the two terms **stacked**, per dimension, sorted by activity — is
 * now unblocked by `D125` and is still deferred by `plan §8.2`. What is drawn here is each vector
 * separately plus the budget total, which is the same information without the stacked geometry.
 */

/**
 * The card's heading (`V79`, P14.11).
 *
 * One constant rather than two literals: it appeared twice -- the loaded panel and the
 * `unavailable` one -- and **nothing asserted either of them**, so the two could have drifted
 * apart silently. `web/test/health.test.ts` now checks this.
 */
export const HEADING = 'active latent dimensions';

/** What `GET /atlases/{id}/latent_stats` returns — `build.latent_stats_payload`'s keys. */
export interface LatentStatsPayload {
  n_rows: number;
  latent_dim: number;
  per_dim_kl: number[];
  /**
   * The **activity** vector, `Var_x[mu(x)_d]` (`D124`). Optional: an atlas built before that
   * change has no such field, and the panel drops the chart rather than the whole reading.
   */
  per_dim_mu_variance?: number[];
  /**
   * The mean encoder variance `E_x[sigma(x)_d^2]` (`D125`). Optional for the same reason as the
   * vector above: an atlas built before that change has no such field.
   */
  per_dim_sigma_sq?: number[];
  'latent/active_units': number;
  'latent/active_fraction': number;
  'latent/collapse_fraction': number;
  'latent/kl_per_dim_mean': number;
  'latent/kl_per_dim_max': number;
  'latent/kl_per_dim_min': number;
  'latent/mu_variance_mean': number;
  'latent/mean_mu_norm': number;
  'latent/mean_sigma': number;
  /** `E[sigma^2]` (`D125`). **Not** the square of `mean_sigma`, which is `E[sigma]`. */
  'latent/sigma_sq_mean'?: number;
}

export interface Verdict {
  level: 'healthy' | 'partial' | 'collapsed' | 'unknown';
  headline: string;
  detail: string;
}

/**
 * **The one piece of judgement in this file, kept in a pure function so it can be tested without
 * a browser.**
 *
 * The thresholds are `LatentStats`'s own, not new ones: a dimension is *collapsed* when its KL
 * falls below `collapse_threshold` (0.01) and *active* when `Var_x(mu)` exceeds
 * `active_threshold` (0.01). Inventing a second set of cut-offs here would mean the panel and the
 * training-time metric could disagree about the same checkpoint.
 *
 * `mean_sigma ≈ 1` alongside a near-zero KL is the signature worth naming explicitly: the encoder
 * is returning the prior, so every input maps to the same distribution and the decoder has learned
 * to ignore `z` entirely.
 */
export function verdictFor(stats: LatentStatsPayload): Verdict {
  const collapsed = stats['latent/collapse_fraction'];
  const active = stats['latent/active_units'];
  const dim = stats.latent_dim;
  const sigma = stats['latent/mean_sigma'];

  if (!Number.isFinite(collapsed) || !Number.isFinite(dim) || dim <= 0) {
    return { level: 'unknown', headline: 'no reading', detail: 'the statistics are unusable' };
  }
  const prior = sigma > 0.95 && stats['latent/kl_per_dim_mean'] < 0.01;
  if (collapsed >= 0.99) {
    return {
      level: 'collapsed',
      headline: 'posterior collapse',
      detail: prior
        ? `all ${dim} dimensions carry ~no information and mean σ is ${sigma.toFixed(3)}: the ` +
          'encoder is returning the prior, so the decoder has learned to ignore z. The map is a ' +
          'picture of noise.'
        : `all ${dim} dimensions fall below the KL threshold LatentStats uses (0.01).`,
    };
  }
  if (collapsed > 0.5) {
    return {
      level: 'partial',
      headline: `${Math.round(collapsed * dim)} of ${dim} dimensions collapsed`,
      detail: `${active} carry information. The map is real but lives in a smaller space than the latent width suggests.`,
    };
  }
  return {
    level: 'healthy',
    headline: `${active} of ${dim} dimensions active`,
    detail: `collapse fraction ${collapsed.toFixed(2)}; mean σ ${sigma.toFixed(3)}.`,
  };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const child of children) node.append(child);
  return node;
}

/**
 * One per-dimension vector as bars, **sorted by decreasing value** and labelled with the original
 * dimension index.
 *
 * Sorted because the shape is the reading: a healthy space is a slope, a collapsed one is a flat
 * line at zero, and dimension order carries no meaning that sorting would destroy. The index is
 * kept on each bar so a dimension can still be found — which is what makes this usable with the
 * raw-dimension view (`V12`), where the reader wants to know *which* pair is worth plotting.
 */
function distributionChart(perDim: number[], label: string): HTMLElement {
  const ranked = perDim.map((value, dim) => ({ value, dim })).sort((a, b) => b.value - a.value);
  const max = ranked[0]?.value ?? 0;
  const chart = el('div', { class: 'kl-chart' });
  for (const { value, dim } of ranked) {
    const bar = el('div', {
      class: 'kl-bar',
      title: `dimension ${dim}: ${label} ${value.toExponential(3)}`,
    });
    // A zero-valued dimension gets a visible sliver rather than nothing, so "32 dead dimensions"
    // reads as 32 things rather than as a missing chart.
    bar.style.height = `${max > 0 ? Math.max(2, (value / max) * 100) : 2}%`;
    chart.append(bar);
  }
  return chart;
}

/**
 * The activity chart, or nothing.
 *
 * **Absent rather than empty on an atlas built before `D124`.** A chart of zeros would say "no
 * dimension varies", which is a reading, and the honest state is that the number was never
 * written.
 */
function activityChart(stats: LatentStatsPayload): HTMLElement[] {
  const activity = stats.per_dim_mu_variance;
  if (activity === undefined || activity.length === 0) return [];
  return [
    distributionChart(activity, 'Var(μ)'),
    el(
      'p',
      { class: 'label' },
      'per-dimension activity Var(μ), sorted — active units is a count off this',
    ),
  ];
}

/**
 * The per-dimension budget, as one row: the range of `Var_x[μ]_d + E_x[σ²]_d` across dimensions.
 *
 * **This is the only figure on the panel that is derived rather than logged**, and it is derived
 * by an exact identity rather than by a model of our own — which is what keeps it inside `V64`'s
 * rule. It is worth a row because it is the sanity check on everything above it: a matched
 * aggregate posterior puts every dimension near 1.0, and a total far from 1 means the two terms
 * are not describing the same latent space.
 */
function budgetRow(stats: LatentStatsPayload): [string, string][] {
  const activity = stats.per_dim_mu_variance;
  const noise = stats.per_dim_sigma_sq;
  if (activity === undefined || noise === undefined || activity.length !== noise.length) return [];
  const totals = activity.map((value, dim) => value + (noise[dim] ?? 0));
  return [
    ['budget per dim', `${Math.min(...totals).toFixed(3)}–${Math.max(...totals).toFixed(3)} (prior 1.0)`],
  ];
}

/** The encoder-variance chart, or nothing. Same optionality rule as `activityChart`. */
function noiseChart(stats: LatentStatsPayload): HTMLElement[] {
  const noise = stats.per_dim_sigma_sq;
  if (noise === undefined || noise.length === 0) return [];
  return [
    distributionChart(noise, 'E[σ²]'),
    el(
      'p',
      { class: 'label' },
      'per-dimension encoder variance E[σ²], sorted — with activity this is a budget summing to ~1',
    ),
  ];
}

export interface Health {
  show(stats: LatentStatsPayload): void;
  unavailable(reason: string): void;
  clear(): void;
}

export function createHealth(root: HTMLElement): Health {
  return {
    show(stats) {
      const verdict = verdictFor(stats);
      const numbers = el('dl', { class: 'properties' });
      const rows: [string, string][] = [
        ['active units', `${stats['latent/active_units']} of ${stats.latent_dim}`],
        ['collapse fraction', stats['latent/collapse_fraction'].toFixed(3)],
        ['KL per dim (mean)', stats['latent/kl_per_dim_mean'].toExponential(3)],
        ['KL per dim (max)', stats['latent/kl_per_dim_max'].toExponential(3)],
        ['KL per dim (min)', stats['latent/kl_per_dim_min'].toExponential(3)],
        ['Var(μ) mean', stats['latent/mu_variance_mean'].toExponential(3)],
        ['mean ‖μ‖', stats['latent/mean_mu_norm'].toFixed(4)],
        ['mean σ', stats['latent/mean_sigma'].toFixed(4)],
        // `E[σ²]`, and it is **not** `mean σ` squared: `E[σ²] ≥ (E[σ])²` by Jensen, with equality
        // only when σ is constant. Two numbers, not one twice.
        ...(stats['latent/sigma_sq_mean'] === undefined
          ? []
          : ([['mean σ²', stats['latent/sigma_sq_mean'].toExponential(4)]] as [string, string][])),
        ...budgetRow(stats),
        // The population the figures describe. See the file docstring.
        ['computed over', `${stats.n_rows.toLocaleString()} rows`],
      ];
      for (const [term, value] of rows) numbers.append(el('dt', {}, term), el('dd', {}, value));

      root.replaceChildren(
        el(
          'section',
          { class: `card health ${verdict.level}` },
          el('h2', {}, HEADING),
          el('p', { class: 'verdict' }, verdict.headline),
          el('p', { class: 'label' }, verdict.detail),
          distributionChart(stats.per_dim_kl, 'KL'),
          el('p', { class: 'label' }, 'per-dimension KL, sorted — hover a bar for its dimension'),
          ...activityChart(stats),
          ...noiseChart(stats),
          numbers,
        ),
      );
    },
    unavailable(reason) {
      // **Says what is missing rather than showing nothing.** An atlas built before the panel
      // existed has no `latent_stats.json`, and a blank panel would read as a healthy one.
      root.replaceChildren(
        el(
          'section',
          { class: 'card health unknown' },
          el('h2', {}, HEADING),
          el('p', { class: 'label' }, reason),
        ),
      );
    },
    clear: () => root.replaceChildren(),
  };
}
